import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import usePagination from "hooks/usePagination";
import useRequest from "hooks/useRequest";

import i18n from "i18n";
import { PublicService } from "services/PublicService.jsx";
import { DeviceService } from "services/DeviceService.js";
import { TagService } from "services/TagService";
import { EventRepository } from "helpers/EventRepository";

import { steps } from "./data/steps";
import Loader from "components/MainApp/atoms/Loader";
import AdminLayout from "components/MainApp/layouts/DesktopLayout";
import StepProgress from "components/MainApp/atoms/Wizard/StepProgress";
import StepZero, { ItemType } from "./Wizard/Step0";
import StepOne from "./Wizard/Step1";
import StepTwo from "./Wizard/Step2";
import StepThree from "./Wizard/Step3";

import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AddConfigTagSuccess from "../AddConfigTagSuccess";

import { isMobile } from "helpers/Mobile";
import { ParseDate, formatDateToYyyyMmDd } from "helpers/Parsers";
import * as XLSX from "xlsx";

import styles from "./AddTag.module.scss";
import { af, be, ca } from "date-fns/locale";
import { Button, styled } from "@material-ui/core";
import { ProductService } from "services/ProductService";
import { blue } from '@mui/material/colors';
import UploadButton from "shared/components/buttons/upload-button";
import Environment from "./../../../../environment";
import BatchTagsErrorModal from "./BatchTagsErrorModal";
import PrintErrorModal from "./PrintErrorModal";
import SatoPrinter from "shared/components/sato-printer/SatoPrinter";
import { TooltipTemplate } from "components/MainApp/atoms/TooltipTemplate/TooltipTemplate";
import { SuccessView } from "./SuccessView/SuccessView";

const initialTagInfo = {
  batch: "",
  due_date: formatDateToYyyyMmDd(new Date()),
  sscc: "",
  origin: "",
  destination: ""
};

const initialError = {
  success: true,
  message: "",
  errors: []
};

const AddTagView = props => {
  const history = useHistory();
  const {
    loading,
    beforeSubmit,
    afterSubmit,
    errors,
    dealWithError
  } = useRequest();
  const [activeStep, setActiveStep] = useState("0");
  const [sentSuccess, setSentSuccess] = useState(false);
  const [printSuccess, setPrintSuccess] = useState(false);
  const [tag, settag] = useState(initialTagInfo);
  const [configTagId, setConfigTagId] = useState();
  const [tagType, setTagType] = useState(null);

  const [productResultList, setproductResultList] = useState([]);
  const [addedList, setaddedList] = useState([]);
  const [selectedToAddProductList, setselectedToAddProductList] = useState([]);
  const [
    list,
    currentPage,
    pageSize,
    pageList,
    pageQuantity,
    changePageSize,
    changePage,
    changeList
  ] = usePagination([], 1, 3);

  const [printer, setPrinter] = useState();
  const [printers, setPrinters] = useState([]);

  const [template, setTemplate] = useState();
  const [templates, setTemplates] = useState([]);

  const [generatedTags, setGeneratedTags] = useState([]);
  const [itemsWithGeneratedTags, setItemsWithGeneratedTags] = useState([]);
  const [modalPersistOpen, setmodalPersistOpen] = useState(false);
  const [showModalErrorFile, setShowModalErrorFile] = useState(false);
  const [showModalPrintError, setShowModalPrintError] = useState(false);
  const [showContinueButton, setShowContinueButton] = useState(true);
  const [errorsFile, setErrorsFile] = useState([]);
  const [errorFile, setErrorFile] = useState(null);

  const headerOptions = [];
  const satoPrinterRef = useRef();

  useEffect(() => {
    DeviceService.devices({ device_type: "PRINTER" }).then(data => {
      data = data.map(device => {
        return { label: device.name, option: device.id };
      });
      setPrinters(data);
    });
  }, []);

  useEffect(() => {
    beforeSubmit();
    setaddedList([]);
    changeList([], 1, 3);

    PublicService.containers({
      page_size: 999999,
      ordering: "-created_at",
      type: tagType
    }).then(response => {
      response.results = response.results.map(product => {
        product.selected = false;
        product.units = 1;
        product.stock = 1;
        return product;
      });
      setproductResultList(response.results);
      afterSubmit();
    }).catch(error => {
      afterSubmit();
      // console.log(error);
    });
  }, [tagType]);

  useEffect(() => {
    if(activeStep === "1" && tagType) {
      beforeSubmit();

      TagService.configTagsPaginated({ page: 1, page_size: 99 })
        .then((response) => {
          const configsTagByType = response.results.filter(result => result.type === tagType);
          setTemplates(configsTagByType.map(configTag => ({
            label: configTag.name,
            option: configTag.id,
            devices: configTag.devices,
            tooltip: <TooltipTemplate data={configTag} />,
          })))
        })
        .catch(() => setActiveStep("0"))
        .finally(() => afterSubmit());
    }
  }, [activeStep, tagType])


  const filterProducts = (value) => {
    if (tagType === ItemType.PRODUCT) {
      if (value.length > 2) {
        return new Promise((resolve, reject) => {
          var filter = [];
          filter["search"] = value;
          beforeSubmit();

          PublicService.products_plain({ page_size: 999999, search: value }).then(
            response => {
              response.results = response.results.map(product => {
                product.selected = false;
                product.units = 1;
                product.stock = 1;
                return product;
              });
              setproductResultList(response.results);
              resolve(response.results);
              afterSubmit();
            }
          ).catch(error => {
            resolve([]);
            afterSubmit();
          });
        });
      }
    }
  }

  const handleGenerateTags = e => {
    e.preventDefault();
    if (
      generatedTags &&
      itemsWithGeneratedTags &&
      generatedTags.length > 0 &&
      itemsWithGeneratedTags.length > 0
    ) {
      setActiveStep("2");
      return;
    }

    beforeSubmit(true);
    let items = addedList.map(item => {
      let data = {
        sku: item.sku,
        quantity: item.units,
        variables: {
          name: item.name,
          batch: tag.batch,
          expiration: tag.due_date,
          date: ParseDate(new Date())
        }
      };

      if (tagType === ItemType.PRODUCT) {
        data["variables"]["description"] = item.description;
        data["variables"]["date"] = ParseDate(new Date());
      } else {
        data["variables"]["sscc"] = tag.sscc;
        data["variables"]["origin"] = tag.origin;
        data["variables"]["destination"] = tag.destination;
      };

      return data;
    });

    TagService.generateTags({ type: tagType, tags: items })
      .then(tags => {
        afterSubmit();
        setGeneratedTags(tags);
        setItemsWithGeneratedTags(organizeTagsByItem(addedList, tags));
        setActiveStep("2");
      })
      .catch(error => {
        afterSubmit();
        dealWithError(error, "generic.error");
      });
  };

  const organizeTagsByItem = (items, tags) => {
    return items.map(item => {
      item.tags = tags.filter(t => t.sku === item.sku);
      return item;
    });
  };

  const handleRemoveTags = () => {
    const data = generatedTags.map(t => {
      return { rfid: t.rfid };
    });

    beforeSubmit();

    TagService.removeTags(data)
      .then(_ => {
        afterSubmit();
        setGeneratedTags([]);
        setItemsWithGeneratedTags([]);
      })
      .catch(error => {
        afterSubmit();
        dealWithError(error, "generic.error", message => {
          EventRepository.notificationSend({
            label: message,
            type: "error"
          });
        });
      });
  };

  const handleFinishAndReceptionOrder = async e => {
    e.preventDefault();
    if (!validateStep2()) return;
    const result = await handleFinish(e, false);
    if (result) {
      handleContinueToOrder();
    } else {
      setShowModalPrintError(true);
    }
  }

  const handleContinueToOrder = () => {
    history.push({
      pathname: "/admin/orders/reception/load",
      state: { generatedTags: generatedTags }
    });
  }

  const handleDownloadTags = () => {
    const data = generatedTags.map(tag => ({
      [i18n.t("product.code")]: tag.sku,
      'TAG': tag.rfid
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, i18n.t("etiquetas"));
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = i18n.t("etiquetas") + '.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  const handleFinish = async (e, printErrorOnTop = true) => {
    e.preventDefault();
    let success = true;
    if (!validateStep2()) return;

    const data = {
      device: printer.option,
      rfids: generatedTags.map(t => t.rfid)
    };

    beforeSubmit();
    try {
      setShowContinueButton(true);
      if (printer.label.indexOf("SATO") !== -1) {
        if (satoPrinterRef.current) {
          const data = {
            device: printer.option,
            config_tag_id: configTagId,
            rfids: generatedTags.map(t => t.rfid)
          };


          await satoPrinterRef.current.sendCommandToPrinter(data);
          afterSubmit();
        }
      } else {
        await TagService.printTags({ ...data, config_tag: configTagId });
      }
      afterSubmit();
      EventRepository.notificationSend({
        label: i18n.t("printing.tags.successfully"),
        type: "success"
      });
      setPrintSuccess(true);
    } catch (error) {
      afterSubmit();
      success = false;
      if (printErrorOnTop) {
        setShowModalPrintError(true);
        setShowContinueButton(false);
      }
    }
    return success;
  };

  const handleClose = () => {
    handleRemoveTags();
    setmodalPersistOpen(false);
    goToList();
  };

  const handleFinishStepTwo = () => {
    beforeSubmit();
    setmodalPersistOpen(false);

    setTimeout(() => {
      afterSubmit();
      setSentSuccess(true);
    }, 200);
  }

  const onlyCloseModal = () => {
    setmodalPersistOpen(false);
  }

  const handlePersist = () => {
    let data = {};
    data.tag = tag;
    data.addedList = addedList;
    data.printer = printer;
    data.printers = printers;
    data.generateTags = generatedTags;
    data.itemsWithGeneratedTags = itemsWithGeneratedTags;
    data.productResultList = productResultList;
    data.selectedToAddProductList = selectedToAddProductList;
    data.activeStep = activeStep;
    setmodalPersistOpen(false);
    goToList();
  };

  const persistenceGuard = () => {
    setmodalPersistOpen(true);
  };

  const persistTagPrintData = data => {
    localStorage.setItem(data.id, data);
  };

  const goToList = () => {
    history.push(`/admin/more-options-tags`);
  };

  const handleChange = e => {
    const name = e.target.name;
    const value = e.target.value;
    let tagAux = { ...tag };
    tagAux[name] = value;

    settag(tagAux);
  };

  const handleSelectPrinter = (selectedOption, type) => {
    setPrinter(selectedOption);
  };

  const handleSelectTemplate = (selectedOption, type) => {
    setTemplate(selectedOption);
    setConfigTagId(selectedOption?.devices[0]?.config_tag)
  };

  const handleSelectToAddProduct = product => {
    let selectedToAddProductListNew = [...selectedToAddProductList];
    let ind = selectedToAddProductListNew.findIndex(p => {
      return p.id === product;
    });
    if (ind !== -1) {
      selectedToAddProductListNew = selectedToAddProductListNew.filter(p => {
        return p.id !== product;
      });
    } else {
      let newProduct = productResultList.find(p => {
        return p.id === product;
      });
      newProduct.selected = true;
      selectedToAddProductListNew.push(newProduct);
    }

    setselectedToAddProductList(selectedToAddProductListNew);
  };

  const handleSelectedProductQuantityChange = (productId, number) => {
    let productResultListNew = [...productResultList];
    let productNew = productResultListNew.find(p => p.id === productId);

    productNew.units = number;
    setproductResultList(productResultListNew);
  };

  const handleAdd = () => {
    let addedListNew = [...addedList];
    selectedToAddProductList.forEach(product => {
      let addedInd = addedList.findIndex(p => {
        return p.id === product.id;
      });
      if (addedInd === -1) {
        addedListNew.push({ ...product });
      } else {
        let productSelected = {
          ...productResultList.find(p => p.id === product.id)
        };
        let productAddedIndex = addedListNew.findIndex(
          p => p.id === product.id
        );
        addedListNew[productAddedIndex].units =
          Number(addedListNew[productAddedIndex].units) +
          Number(productSelected.units);
      }

      setaddedList(addedListNew);
      changeList(addedListNew, currentPage, pageSize);
    });

    let productResultListNew = productResultList.map(p => {
      p.units = 1;
      return p;
    });

    setproductResultList(productResultListNew);
    setselectedToAddProductList([]);
  };

  const handleAddProduct = (productId, number) => {
    number = number === 0 ? 1 : number;

    let addedListNew = [...addedList];
    let productAddedIndex = addedListNew.findIndex(p => p.id === productId);
    addedListNew[productAddedIndex].units = Number(number);
    setaddedList(addedListNew);
    changeList(addedListNew, currentPage, pageSize);
  };

  const handleDeleteAddedProduct = productId => {
    let addedListNew = [...addedList];

    addedListNew = addedListNew.filter(p => p.id !== productId);
    setaddedList(addedListNew);
    changeList(addedListNew, currentPage, pageSize);
  };

  const validateStep0 = () => {
    if (tag) {
      return (
        tag.batch &&
        tag.batch !== "" &&
        tag.due_date &&
        tag.due_date !== "" &&
        addedList.length > 0
      );
    } else {
      return false;
    }
  };

  const validateStep1 = () => {
    if (tag) {
      return tag.width !== "" && tag.height !== "";
    } else {
      return false;
    }
  };

  const validateStep2 = () => {
    if (tag) return printer;
    return false;
  };

  const goToStep = step => {
    setActiveStep(step);
  };

  const resetTags = () => {
    handleRemoveTags();
    setActiveStep("1");
  };

  const validateFile = file => {
    // si el archivo no es xlsx o ods lanzamos error
    if (!file.name.match(/\.(xlsx|ods)$/)) {
      EventRepository.notificationSend({
        label: i18n.t("message.excel.wrong.file"),
        type: "error"
      });
      return false;
    }
    return true;
  }

  const validateHeadersFile = (sheet) => {
    if (sheet["A1"]?.v?.toLowerCase() !== "codigo" || sheet["B1"]?.v?.toLowerCase() !== "cantidad") {
      EventRepository.notificationSend({
        label: i18n.t("file.must.have.code.quantity"),
        type: "error"
      });
      return false;
    }
    return true;
  }

  const insertProducts = (sheetData) => {
    sheetData.splice(0, 1); // eliminamos la cabecera
    let first_row_data = 2;
    const cleanProducts = [];

    // si la celda incluye la palabera alfamumérico, eliminamos la fila
    if (sheetData[0][0]?.toString().toLowerCase().includes("alfanumérico")) {
      sheetData.splice(0, 1);
      first_row_data = 3;
    }

    sheetData.forEach((row) => {
      if (row && row.length > 0 && row[0] != "") {
        let product = {
          codigo: row[0]?.toString(),
          count: row[1]
        }
        const productAdded = cleanProducts.find(p => p.codigo === product.codigo);
        if (productAdded) {
          productAdded.count = productAdded.count + product.count;
        } else {
          cleanProducts.push(product)
        }
      }
    });

    if (cleanProducts.length === 0) {
      EventRepository.notificationSend({
        label: i18n.t("empty.file"),
        type: "error"
      });
    }

    loadProductsBySKU(cleanProducts, first_row_data);
  }

  const loadProductsBySKU = async (cleanProducts, first_row_data) => {
    setErrorsFile([]);
    let addedListNew = [...addedList];
    let errors = [];
    let row_data = first_row_data;
    beforeSubmit();
    for (const p of cleanProducts) {
      const row_number_aux = row_data;
      row_data++;
      try {
        // si el codigo está vacío se lanza error
        p.codigo = p?.codigo?.trim();
        if (!p.codigo) {
          throw new Error(i18n.t("code.is.missing"));
        }
        p.count = String(p.count)?.replace(',', '.');
        if (isNaN(Number(p.count))) {
          throw new Error(i18n.t("valid.number.is.required"));
        }

        let result = await ProductService.itemBySKU(p.codigo, tagType);
        if (!result.results || result.results.length === 0) {
          throw new Error(i18n.t("sku.does.not.exist").concat(": ", p.codigo));
        }

        let product = result.results[0];
        product["selected"] = true;
        product["units"] = Number(p.count);
        product["stock"] = Number(p.count);

        let productAddedIndex = addedListNew.findIndex(item => item.id === product.id);

        if (productAddedIndex > -1) {
          addedListNew[productAddedIndex].units += Number(p.count);
          addedListNew[productAddedIndex].stock += Number(p.count);
        } else {
          addedListNew.push(product);
        }
      } catch (error) {
        errors.push({ fila: row_number_aux, error: error.message });
      }
    }
    afterSubmit();

    if (errors.length > 0) {
      // Generar el fichero XLSX con los errores
      const ws = XLSX.utils.json_to_sheet(errors);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Errores");
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/octet-stream' });
      const errorFileUrl = URL.createObjectURL(blob);
      setErrorFile(errorFileUrl);
      setShowModalErrorFile(true);
    } else {
      setErrorsFile([]);
      setaddedList(addedListNew);
      changeList(addedListNew, currentPage, pageSize); // Asegúrate de que esto también esté basado en el estado actualizado
    }
  };

  const downloadErrorFile = () => {
    const link = document.createElement('a');
    link.href = errorFile;
    link.download = 'errores.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const headerTitle = sentSuccess ? i18n.t("configtag.created") : i18n.t("generate.and.print.tags");

  const downloadTemplate = (e) => {
    e.preventDefault();
    window.open(Environment.statics + "files/impresiontags.xlsx", "_blank");
  };

  const step0 = (
    <StepZero
      tagType={tagType}
      setTagType={setTagType}
      handleBack={goToList}
      handleContinue={() => setActiveStep("1")}
    />
  )

  const step1 = (
    <React.Fragment>
      <StepOne
        handleChange={handleChange}
        resultList={productResultList}
        searchValue={""}
        products={isMobile ? addedList : pageList}
        totalProducts={addedList.length}
        handleSelectToAddProduct={handleSelectToAddProduct}
        handleSelectedProductQuantityChange={handleSelectedProductQuantityChange}
        handleAdd={handleAdd}
        handleAddProduct={handleAddProduct}
        selectedProducts={selectedToAddProductList.map(p => p.id)}
        handleDeleteAddedProduct={handleDeleteAddedProduct}
        changePage={changePage}
        pagination={{
          pageQuantity: pageQuantity,
          currentPage: currentPage
        }}
        handleContinue={handleGenerateTags}
        enableContinue={validateStep0()}
        type={tagType}
        tag={tag}
        error={errors}
        handleClose={handleClose}
        handlePersist={handlePersist}
        onlyCloseModal={onlyCloseModal}
        modalPersistOpen={modalPersistOpen}
        filter={filterProducts}
        validateFile={validateFile}
        validateHeadersFile={validateHeadersFile}
        insertProducts={insertProducts}
        errorFile={errorFile}
        setShowModalErrorFile={setShowModalErrorFile}
        downloadErrorFile={downloadErrorFile}
        changeList={changeList}
        tagType={tagType}
      />

      {/* Modal para mostrar los posible errores del fichero de importación  */}
      <BatchTagsErrorModal
        open={showModalErrorFile}
        handleClose={() => setShowModalErrorFile(false)}
        handleDownload={downloadErrorFile}
      />

    </React.Fragment>

  );

  const step2 = (
    <StepTwo
      handleContinue={() => setActiveStep("3")}
      handleEdit={() => resetTags()}
      enableContinue={validateStep1()}
      tag={tag}
      error={errors}
      addedProducts={itemsWithGeneratedTags}
      handleClose={handleClose}
      handlePersist={handlePersist}
      modalPersistOpen={modalPersistOpen}
      onlyCloseModal={onlyCloseModal}
      handleFinishStepTwo={handleFinishStepTwo}
      handleDownloadTags={handleDownloadTags}
    />
  );

  const step3 = (
    <div>
      <StepThree
        handleFinish={handleFinish}
        goBackFunc={() => goToStep("2")}
        printer={printer}
        handleSelectPrinter={handleSelectPrinter}
        template={template}
        setTemplate={setTemplate}
        handleSelectTemplate={handleSelectTemplate}
        error={errors}
        printers={printers}
        templates={templates}
        enableContinue={validateStep2() && template}
        handleClose={handleClose}
        handlePersist={handlePersist}
        modalPersistOpen={modalPersistOpen}
        onlyCloseModal={onlyCloseModal}
        handleFinishAndReceptionOrder={handleFinishAndReceptionOrder}
        configTagId={configTagId}
      />

      <PrintErrorModal
        open={showModalPrintError}
        handleClose={() => setShowModalPrintError(false)}
        handleContinue={handleContinueToOrder}
        handleDownloadTags={handleDownloadTags}
        showContinue={showContinueButton}
      />
    </div>
  );

  // const stepList = [step0, step1, step2];
  const stepList = {
    "0": step0,
    "1": step1,
    "2": step2,
    "3": step3
  };

  const saveSuccessMessage = i18n.t("tags.saved.successfully")
  const printSuccessMessage = i18n.t("tags.printed.successfully")

  const successView =
    <SuccessView
      tags={itemsWithGeneratedTags}
      title={printSuccess ? printSuccessMessage : saveSuccessMessage}
    />

  const stepsWithNavigation = steps.map(step => ({
    ...step,
    onClick: () => setActiveStep(step.id)
  }))

  const content = (
    <div className={styles["add-tag-content"]}>
      {!sentSuccess && (
        <div className={styles["add-tag-wrapper"]}>
          <StepProgress steps={stepsWithNavigation} activeStep={activeStep} isNavigable />
          <div className={styles["add-tag-step-wrapper"]}>{stepList[activeStep]}</div>
        </div>
      )}
      <SatoPrinter ref={satoPrinterRef} />
    </div>
  );

  return (
    <div className={styles["add-tag-container"]}>
      <AdminLayout
        headerTitle={i18n.t("generate.and.print.tags")}
        headerOptions={headerOptions}
        content={sentSuccess || printSuccess ? successView : content}
        navHidden={true}
        goBackFunc={goToList}
        isScrollable={true}
      ></AdminLayout>
      {loading && <Loader />}
    </div>
  );
};

export default AddTagView;
